import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { analyzeMessage } from '../utils/scamAnalyzer';
import { getApiUrl } from '../config';

// Scam Check for the web dashboard. The message is analyzed in the browser;
// only links found in it are sent to the server for a Google Safe Browsing check.

const LEVELS = {
  high: { label: 'Several scam warning signs', color: '#ff3b5c' },
  medium: { label: 'Some warning signs', color: '#ffaa00' },
  low: { label: 'No common warning signs found', color: '#00d68f' },
};
const THREAT_STATUSES = ['malicious', 'suspicious', 'phishing', 'malware', 'threat'];

const cardStyle = {
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(0, 212, 255, 0.2)',
  borderRadius: '1.5rem',
};

const ScamCheckWeb = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [links, setLinks] = useState([]);
  const [checking, setChecking] = useState(false);

  const run = async () => {
    if (!text.trim()) return;
    const analysis = analyzeMessage(text);
    setResult(analysis);
    setLinks([]);
    if (!analysis.links.length) return;
    setChecking(true);
    const checked = await Promise.all(analysis.links.map(async (url) => {
      try {
        const res = await fetch(`${getApiUrl()}/api/scan/url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        });
        const data = await res.json();
        const state = THREAT_STATUSES.includes(data.status) ? 'flagged' : data.status === 'clean' ? 'clean' : 'error';
        return { url, state };
      } catch (e) {
        return { url, state: 'error' };
      }
    }));
    setLinks(checked);
    setChecking(false);
  };

  const level = result ? (links.some(l => l.state === 'flagged') ? 'high' : result.level) : null;

  return (
    <div className="space-y-6">
      <Card style={cardStyle}>
        <CardHeader>
          <CardTitle style={{ color: '#ffffff' }}>Scam Check</CardTitle>
          <CardDescription style={{ color: '#cbd5e1' }}>
            Paste a text, email or message you're unsure about. CyberGuard looks for common scam warning signs and checks any links with Google Safe Browsing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <textarea
            className="w-full min-h-[160px] rounded-lg p-3 text-white"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0, 212, 255, 0.3)' }}
            placeholder="Paste the message here"
            value={text}
            onChange={e => setText(e.target.value)}
            aria-label="Message to check"
          />
          <Button onClick={run} disabled={!text.trim()}>Check message</Button>
          <p className="text-sm" style={{ color: '#94a3b8' }}>The message is analyzed in your browser and isn't stored. Only links are sent for checking.</p>
        </CardContent>
      </Card>

      {result && level && (
        <Card style={{ ...cardStyle, borderLeft: `4px solid ${LEVELS[level].color}` }}>
          <CardHeader>
            <CardTitle style={{ color: LEVELS[level].color }}>{LEVELS[level].label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4" style={{ color: '#e2e8f0' }}>
            {result.findings.map(f => (
              <div key={f.id}>
                <p className="font-semibold text-white">• {f.title}</p>
                <p className="text-sm" style={{ color: '#cbd5e1' }}>{f.detail}</p>
                {f.evidence && <p className="text-sm italic" style={{ color: '#67e8f9' }}>Found: "{f.evidence}"</p>}
              </div>
            ))}
            {result.links.length > 0 && (
              <div>
                <p className="font-semibold text-white">Links in this message</p>
                {checking && <p className="text-sm">Checking links…</p>}
                {links.map(l => (
                  <p key={l.url} className="text-sm break-all" style={{ color: l.state === 'flagged' ? '#ff3b5c' : l.state === 'clean' ? '#00d68f' : '#cbd5e1' }}>
                    {l.state === 'flagged' ? '⚠️ Flagged as unsafe: ' : l.state === 'clean' ? '✓ Not on known threat lists: ' : "Couldn't check: "}{l.url}
                  </p>
                ))}
              </div>
            )}
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              {level === 'low'
                ? "No warning signs doesn't guarantee a message is safe. If it asks for money or personal details, contact the company through its official website or app."
                : "Don't reply, click links or send money. Contact the company or person directly using a phone number or website you already trust."}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ScamCheckWeb;
